import {
  useCallback,
  useEffect,
  useRef,
  useState,
  type CSSProperties,
  type ReactNode,
} from 'react';
import { Link, useRouterState } from '@tanstack/react-router';
import { useAuth } from '@rudi/auth';
import { Button } from '@rudi/ui/button';
import { cn } from '@rudi/ui/lib/utils';
import {
  Activity,
  Bot,
  Building2,
  Cpu,
  ChevronDown,
  Mic,
  Flag,
  KeyRound,
  ListChecks,
  LogOut,
  Menu,
  MessagesSquare,
  ClipboardList,
  MessageSquareWarning,
  Gauge,
  HeartPulse,
  Monitor,
  PanelLeftClose,
  PanelLeftOpen,
  ScanSearch,
  Server,
  Video,
  Users,
  Workflow,
  Wallet,
  QrCode,
  X,
} from 'lucide-react';
import { clearSession } from '../platform/auth-storage';
import { readTargetId } from '../platform/target';
import { TenantNavigation } from './TenantNavigation';
import { TargetSwitcher, useTargets } from './TargetSwitcher';

interface NavItem {
  to: string;
  label: string;
  icon: ReactNode;
}

const NAV: NavItem[] = [
  { to: '/clusters', label: 'Clusters', icon: <Server className="h-4 w-4" /> },
  { to: '/uptime', label: 'Uptime', icon: <HeartPulse className="h-4 w-4" /> },
  { to: '/traces', label: 'AI traces', icon: <Activity className="h-4 w-4" /> },
  { to: '/chat-traces', label: 'Chat traces', icon: <MessagesSquare className="h-4 w-4" /> },
  { to: '/qr-codes', label: 'QR codes', icon: <QrCode className="h-4 w-4" /> },
  { to: '/tenants', label: 'Tenants', icon: <Building2 className="h-4 w-4" /> },
  {
    to: '/clinical-trials',
    label: 'Clinical changes',
    icon: <ClipboardList className="h-4 w-4" />,
  },
  {
    to: '/practice-agents',
    label: 'Practice computers',
    icon: <Monitor className="h-4 w-4" />,
  },
  {
    to: '/prescreening',
    label: 'Pre-screening',
    icon: <ListChecks className="h-4 w-4" />,
  },
  {
    to: '/data-quality',
    label: 'Medical data quality',
    icon: <ScanSearch className="h-4 w-4" />,
  },
  { to: '/users', label: 'Users and sign-ups', icon: <Users className="h-4 w-4" /> },
  { to: '/features', label: 'Features', icon: <Flag className="h-4 w-4" /> },
  { to: '/feedback', label: 'Feedback', icon: <MessageSquareWarning className="h-4 w-4" /> },
  { to: '/replays', label: 'Replays', icon: <Video className="h-4 w-4" /> },
  { to: '/performance', label: 'Performance', icon: <Gauge className="h-4 w-4" /> },
  { to: '/spend', label: 'Spend', icon: <Wallet className="h-4 w-4" /> },
];

const AI_NAV: NavItem[] = [
  { to: '/models', label: 'Rudi', icon: <Cpu className="h-4 w-4" /> },
  {
    to: '/automation-models',
    label: 'Automation models',
    icon: <Workflow className="h-4 w-4" />,
  },
  { to: '/chat-model', label: 'Patient Chat', icon: <Bot className="h-4 w-4" /> },
  { to: '/speech', label: 'Speech recognition', icon: <Mic className="h-4 w-4" /> },
  { to: '/api-keys', label: 'Providers and credentials', icon: <KeyRound className="h-4 w-4" /> },
];

const WIDTH_STORAGE_KEY = 'cortex:sidebar-width';
const MIN_SIDEBAR_WIDTH = 240;
const MAX_SIDEBAR_WIDTH = 640;

function clampSidebarWidth(width: number): number {
  return Math.min(MAX_SIDEBAR_WIDTH, Math.max(MIN_SIDEBAR_WIDTH, width));
}

function readStoredWidth(): number {
  try {
    const stored = window.localStorage.getItem(WIDTH_STORAGE_KEY);
    const width = stored === null ? MIN_SIDEBAR_WIDTH : Number(stored);
    return Number.isFinite(width) ? clampSidebarWidth(width) : MIN_SIDEBAR_WIDTH;
  } catch {
    return MIN_SIDEBAR_WIDTH;
  }
}

const COLLAPSED_STORAGE_KEY = 'cortex:sidebar-collapsed';

function readStoredCollapsed(): boolean {
  if (typeof window === 'undefined') return false;
  try {
    return window.localStorage.getItem(COLLAPSED_STORAGE_KEY) === 'true';
  } catch {
    return false;
  }
}

/**
 * The sidebar has two independent axes so one shell works on both widths:
 * `collapsed` shrinks it to an icon rail from `md` up (persisted), while
 * `mobileOpen` slides it in as an overlay below `md`, where the rail would
 * still eat too much of a phone screen.
 */
export function AppShell({ children }: { children: ReactNode }) {
  const { user, logout } = useAuth();
  // The switcher appears once there is more than one Rudi to choose from.
  const targets = useTargets();
  const showTargets = targets.length > 1 || !!readTargetId();
  const [collapsed, setCollapsed] = useState(readStoredCollapsed);
  const [mobileOpen, setMobileOpen] = useState(false);
  const pathname = useRouterState({ select: state => state.location.pathname });
  const [aiOpen, setAiOpen] = useState(() => AI_NAV.some(item => item.to === pathname));
  useEffect(() => {
    if (AI_NAV.some(item => item.to === pathname)) setAiOpen(true);
  }, [pathname]);
  const [sidebarWidth, setSidebarWidth] = useState(readStoredWidth);
  const [resizing, setResizing] = useState(false);
  const drag = useRef<{ startX: number; width: number } | null>(null);

  const resizeSidebar = (width: number) => {
    const next = clampSidebarWidth(width);
    setSidebarWidth(next);
    try {
      window.localStorage.setItem(WIDTH_STORAGE_KEY, String(next));
    } catch {
      // Resizing still works when browser storage is unavailable.
    }
  };

  const toggleCollapsed = useCallback(() => {
    setCollapsed(previous => {
      const next = !previous;
      try {
        window.localStorage.setItem(COLLAPSED_STORAGE_KEY, String(next));
      } catch {
        // Private-mode storage failures should not block the toggle.
      }
      return next;
    });
  }, []);

  useEffect(() => {
    if (!mobileOpen) return;
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setMobileOpen(false);
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [mobileOpen]);

  return (
    <div
      className={cn('flex h-screen overflow-hidden', resizing && 'cursor-col-resize select-none')}
      style={{ '--sidebar-width': `${sidebarWidth}px` } as CSSProperties}
    >
      {mobileOpen && (
        <button
          type="button"
          aria-label="Close navigation"
          className="fixed inset-0 z-30 bg-foreground/40 md:hidden"
          onClick={() => setMobileOpen(false)}
        />
      )}

      <aside
        className={cn(
          'fixed inset-y-0 left-0 z-40 flex w-60 shrink-0 flex-col border-r bg-card transition-[width,transform] duration-200',
          'md:relative md:w-[min(var(--sidebar-width),calc(100vw-320px))] md:translate-x-0',
          resizing && 'transition-none',
          mobileOpen ? 'translate-x-0' : '-translate-x-full',
          collapsed && 'md:w-16'
        )}
      >
        <div
          className={cn(
            'flex items-center gap-2 px-5 py-4',
            collapsed && 'md:justify-center md:px-2'
          )}
        >
          <div className={cn('min-w-0 flex-1', collapsed && 'md:hidden')}>
            <div className="text-lg font-semibold tracking-tight">Cortex</div>
            <div className="text-xs text-muted-foreground">Super admin</div>
            {showTargets && (
              <TargetSwitcher targets={targets} id="sidebar-rudi-target" className="mt-2" />
            )}
          </div>
          <Button
            variant="ghost"
            size="icon"
            className="hidden md:inline-flex"
            aria-label={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
            title={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
            onClick={toggleCollapsed}
          >
            {collapsed ? (
              <PanelLeftOpen className="h-4 w-4" />
            ) : (
              <PanelLeftClose className="h-4 w-4" />
            )}
          </Button>
          <Button
            variant="ghost"
            size="icon"
            className="md:hidden"
            aria-label="Close navigation"
            onClick={() => setMobileOpen(false)}
          >
            <X className="h-4 w-4" />
          </Button>
        </div>

        <nav aria-label="Main navigation" className="flex-1 space-y-1 overflow-y-auto px-2">
          <details
            className="group"
            open={aiOpen}
            onToggle={event => setAiOpen(event.currentTarget.open)}
          >
            <summary
              className="flex cursor-pointer list-none items-center gap-3 rounded-3xl px-3 py-2 text-sm text-muted-foreground hover:bg-accent"
              title="AI"
            >
              <Bot className="h-4 w-4 shrink-0" />
              <span className={cn('flex-1', collapsed && 'md:hidden')}>AI</span>
              <ChevronDown
                className={cn(
                  'h-4 w-4 transition-transform group-open:rotate-180',
                  collapsed && 'md:hidden'
                )}
              />
            </summary>
            <details
              open={
                pathname === '/models' ||
                pathname === '/chat-model' ||
                pathname === '/automation-models'
              }
            >
              <summary
                className="ml-2 cursor-pointer rounded-3xl px-3 py-2 text-sm"
                title="Language models"
              >
                <span className={cn(collapsed && 'md:hidden')}>Language models</span>
                <Cpu
                  className={cn('h-4 w-4', !collapsed && 'hidden', collapsed && 'hidden md:block')}
                />
              </summary>
              {AI_NAV.filter(item =>
                ['/models', '/chat-model', '/automation-models'].includes(item.to)
              ).map(item => (
                <Link
                  key={item.to}
                  to={item.to}
                  title={item.label}
                  onClick={() => setMobileOpen(false)}
                  className="ml-4 flex items-center gap-3 rounded-3xl px-3 py-2 text-sm text-muted-foreground hover:bg-accent"
                  activeProps={{
                    className:
                      'ml-4 flex items-center gap-3 rounded-3xl bg-accent px-3 py-2 text-sm font-medium',
                  }}
                >
                  {item.icon}
                  <span className={cn('truncate', collapsed && 'md:hidden')}>{item.label}</span>
                </Link>
              ))}
            </details>
            <details open={pathname === '/speech'}>
              <summary
                className="ml-2 flex cursor-pointer items-center gap-3 rounded-3xl px-3 py-2 text-sm"
                title="Speech recognition"
              >
                <Mic className="h-4 w-4 shrink-0" />
                <span className={cn(collapsed && 'md:hidden')}>Speech recognition</span>
              </summary>
              {(['rudi', 'chat'] as const).map(application => (
                <Link
                  key={application}
                  to="/speech"
                  search={{ application }}
                  title={
                    application === 'rudi'
                      ? 'Rudi speech recognition'
                      : 'Patient Chat speech recognition'
                  }
                  onClick={() => setMobileOpen(false)}
                  activeOptions={{ includeSearch: true }}
                  className="ml-4 flex items-center gap-3 rounded-3xl px-3 py-2 text-sm text-muted-foreground hover:bg-accent"
                  activeProps={{
                    className:
                      'ml-4 flex items-center gap-3 rounded-3xl bg-accent px-3 py-2 text-sm font-medium',
                  }}
                >
                  <Mic className="h-4 w-4 shrink-0" />
                  <span className={cn(collapsed && 'md:hidden')}>
                    {application === 'rudi' ? 'Rudi' : 'Patient Chat'}
                  </span>
                </Link>
              ))}
            </details>
            <Link
              to="/api-keys"
              title="Providers and credentials"
              onClick={() => setMobileOpen(false)}
              className="ml-2 flex items-center gap-3 rounded-3xl px-3 py-2 text-sm text-muted-foreground hover:bg-accent"
              activeProps={{
                className:
                  'ml-2 flex items-center gap-3 rounded-3xl bg-accent px-3 py-2 text-sm font-medium',
              }}
            >
              <KeyRound className="h-4 w-4 shrink-0" />
              <span className={cn(collapsed && 'md:hidden')}>Providers and credentials</span>
            </Link>
          </details>
          {[
            { label: 'Organizations', paths: ['/tenants', '/users', '/practice-agents'] },
            {
              label: 'Clinical operations',
              paths: ['/clinical-trials', '/prescreening', '/data-quality', '/qr-codes'],
            },
            {
              label: 'Observability',
              paths: ['/traces', '/chat-traces', '/replays', '/performance', '/spend'],
            },
            { label: 'Platform', paths: ['/clusters', '/uptime', '/features', '/feedback'] },
          ].map(group => (
            <section key={group.label} aria-label={group.label}>
              <p
                className={cn(
                  'px-3 pb-1 pt-4 text-xs font-medium text-muted-foreground',
                  collapsed && 'md:hidden'
                )}
              >
                {group.label}
              </p>
              {group.paths
                .map(path => NAV.find(item => item.to === path)!)
                .map(item => {
                  if (item.to === '/tenants')
                    return (
                      <TenantNavigation
                        key={item.to}
                        collapsed={collapsed}
                        onNavigate={() => setMobileOpen(false)}
                      />
                    );
                  const shared = cn(
                    'flex items-center gap-3 rounded-3xl px-3 py-2 text-sm transition-colors',
                    collapsed && 'md:justify-center md:px-0'
                  );
                  return (
                    <Link
                      key={item.to}
                      to={item.to}
                      title={collapsed ? item.label : undefined}
                      onClick={() => setMobileOpen(false)}
                      className={cn(
                        shared,
                        'text-muted-foreground hover:bg-accent hover:text-accent-foreground'
                      )}
                      activeProps={{
                        className: cn(shared, 'font-medium bg-accent text-accent-foreground'),
                      }}
                    >
                      {item.icon}
                      <span className={cn('truncate', collapsed && 'md:hidden')}>{item.label}</span>
                    </Link>
                  );
                })}
            </section>
          ))}
        </nav>

        <div className={cn('border-t px-3 py-3', collapsed && 'md:px-2')}>
          <div
            className={cn(
              'mb-2 truncate px-1 text-xs text-muted-foreground',
              collapsed && 'md:hidden'
            )}
          >
            {user?.email ?? user?.username}
          </div>
          <Button
            variant="ghost"
            size="sm"
            className={cn('w-full justify-start gap-2', collapsed && 'md:justify-center md:px-0')}
            title={collapsed ? 'Sign out' : undefined}
            onClick={async () => {
              await logout();
              clearSession();
            }}
          >
            <LogOut className="h-4 w-4" />
            <span className={cn(collapsed && 'md:hidden')}>Sign out</span>
          </Button>
        </div>
        {!collapsed && (
          <div
            role="separator"
            aria-label="Sidebar width"
            aria-orientation="vertical"
            aria-valuemin={MIN_SIDEBAR_WIDTH}
            aria-valuemax={MAX_SIDEBAR_WIDTH}
            aria-valuenow={sidebarWidth}
            tabIndex={0}
            title="Drag to resize sidebar, or use the left and right arrow keys"
            className="absolute inset-y-0 -right-1 z-50 hidden w-2 cursor-col-resize touch-none hover:bg-primary/20 focus-visible:bg-primary/20 focus-visible:outline-none md:block"
            onPointerDown={event => {
              if (event.button !== 0) return;
              event.preventDefault();
              const width = event.currentTarget.parentElement!.getBoundingClientRect().width;
              drag.current = { startX: event.clientX, width };
              event.currentTarget.setPointerCapture(event.pointerId);
              setResizing(true);
            }}
            onPointerMove={event => {
              if (!drag.current) return;
              resizeSidebar(drag.current.width + event.clientX - drag.current.startX);
            }}
            onPointerUp={event => {
              if (event.currentTarget.hasPointerCapture(event.pointerId)) {
                event.currentTarget.releasePointerCapture(event.pointerId);
              }
            }}
            onLostPointerCapture={() => {
              drag.current = null;
              setResizing(false);
            }}
            onKeyDown={event => {
              let width = sidebarWidth;
              if (event.key === 'ArrowLeft') width -= 20;
              else if (event.key === 'ArrowRight') width += 20;
              else if (event.key === 'Home') width = MIN_SIDEBAR_WIDTH;
              else if (event.key === 'End') width = MAX_SIDEBAR_WIDTH;
              else return;
              event.preventDefault();
              resizeSidebar(width);
            }}
          />
        )}
      </aside>

      <div className="flex min-w-0 flex-1 flex-col overflow-hidden">
        <header className="flex items-center gap-2 border-b bg-card px-3 py-2 md:hidden">
          <Button
            variant="ghost"
            size="icon"
            aria-label="Open navigation"
            onClick={() => setMobileOpen(true)}
          >
            <Menu className="h-4 w-4" />
          </Button>
          <span className="text-sm font-semibold tracking-tight">Cortex</span>
        </header>

        <main className="flex-1 overflow-auto">
          <div className="mx-auto max-w-6xl p-4 md:p-8">{children}</div>
        </main>
      </div>
    </div>
  );
}
